import { CreditNoteLineItemInputSchema } from './api/credit-note.js';
import { allocateInvoiceDiscounts, isDiscountLine, lineItemVatMinorUnits } from './invoice-totals.js';
/**
 * Per-line credit positions, keyed by invoice line id, for non-discount lines
 * only. The server's per-line cap reads this; a client showing the same
 * positions should call it too rather than re-derive them.
 *
 * Only allocated discounts are netted (the clone's own allocation call). A
 * legacy discount row (negative, no exact VAT) is left un-netted, which is why
 * linked credits are refused on invoices that carry one. VOIDED credit notes
 * are ignored, as are credit lines that are unlinked or linked to a discount
 * row or an id not on this invoice.
 */
export function invoiceLineCreditPositions(invoiceLines, creditNotes) {
    const { allocations } = allocateInvoiceDiscounts(invoiceLines, false, true);
    const credited = new Map();
    for (const creditNote of creditNotes) {
        if (creditNote.status === 'VOIDED')
            continue;
        for (const line of creditNote.lineItems) {
            if (line.invoiceLineItemId === null)
                continue;
            const sum = credited.get(line.invoiceLineItemId) ?? { amount: 0, vat: 0 };
            sum.amount += line.totalAmountMinorUnits;
            sum.vat += lineItemVatMinorUnits(line);
            credited.set(line.invoiceLineItemId, sum);
        }
    }
    const positions = new Map();
    invoiceLines.forEach((line, index) => {
        if (isDiscountLine(line))
            return;
        const allocation = allocations[index];
        const nettedAmountMinorUnits = line.totalAmountMinorUnits - allocation.amountMinorUnits;
        const nettedVatMinorUnits = lineItemVatMinorUnits(line) - allocation.vatAmountMinorUnits;
        const { amount, vat } = credited.get(line.id) ?? { amount: 0, vat: 0 };
        positions.set(line.id, {
            invoiceLineItemId: line.id,
            nettedAmountMinorUnits,
            nettedVatMinorUnits,
            creditedAmountMinorUnits: amount,
            creditedVatMinorUnits: vat,
            remainingAmountMinorUnits: Math.max(0, nettedAmountMinorUnits - amount),
            remainingVatMinorUnits: Math.max(0, nettedVatMinorUnits - vat),
        });
    });
    return positions;
}
/**
 * The lines a credit can link to, in invoice order, each numbered and carrying
 * its `invoiceLineCreditPositions` entry. Discount rows are skipped and not
 * numbered. Every surface that names a line by number reads it from here, so
 * the CLI's `--line N`, the dialog and the server's over-cap message agree.
 */
export function creditableInvoiceLines(invoiceLines, creditNotes) {
    const positions = invoiceLineCreditPositions(invoiceLines, creditNotes);
    const creditable = [];
    for (const line of invoiceLines) {
        const position = positions.get(line.id);
        if (position)
            creditable.push({ line, lineNumber: creditable.length + 1, position });
    }
    return creditable;
}
/**
 * What is left to credit on the whole invoice: its gross total less the
 * totals of its non-VOIDED credit notes. The server's invoice-level cap reads
 * this; a client showing the same figure should call it too rather than re-sum.
 * Not floored at zero, so an over-credit that slipped past the unlocked cap
 * shows as a negative figure.
 */
export function invoiceCreditCapacityMinorUnits(invoice, creditNotes) {
    const credited = creditNotes
        .filter((creditNote) => creditNote.status !== 'VOIDED')
        .reduce((sum, creditNote) => sum + creditNote.totalMinorUnits, 0);
    return invoice.totalMinorUnits - credited;
}
/**
 * VAT for a linked credit of `amountMinorUnits` against one invoice line. A
 * credit that uses up the line's remaining net takes all its remaining VAT, so
 * a line credited in parts ends on exactly its netted VAT. Any other credit
 * takes rate VAT, capped at the remaining VAT so rounding on earlier parts can
 * never force a negative completing VAT.
 */
export function linkedCreditVatMinorUnits(position, vatRateBasisPoints, amountMinorUnits) {
    if (amountMinorUnits === position.remainingAmountMinorUnits)
        return position.remainingVatMinorUnits;
    const rateVat = lineItemVatMinorUnits({ totalAmountMinorUnits: amountMinorUnits, vatRateBasisPoints });
    return Math.min(rateVat, position.remainingVatMinorUnits);
}
/**
 * The description for a credit line linked to an invoice line: the line's own
 * narration, or `Credit for line N` when it is blank or longer than a credit
 * note line accepts. The length bound is the request schema's own rule, so the
 * fallback fires exactly where the server would otherwise refuse the body.
 */
export function linkedCreditLineDescription(description, lineNumber) {
    const usable = description.trim() !== '' && CreditNoteLineItemInputSchema.shape.description.safeParse(description).success;
    return usable ? description : `Credit for line ${lineNumber}`;
}
